import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { menuCategoriesData } from "./constants"; // Import constants

import styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "store/auth/thunks";
import { useLanguage } from "context";
import { selectUserType } from "store/auth/selectors";

const DashboardMenu = ({ closeSidebar = () => {}, isMobileView = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const { language } = useLanguage(); // Get the current language code
  const userType = useSelector(selectUserType);
  const currentUser = useSelector((state) => state.auth.currentUser); // Assuming user data is stored in Redux
  const selectedMenuCategories = (menuCategoriesData[userType] || [])
    .filter((category) => {
      // If user is a provider trainee, exclude the "facturation" category
      if (userType === "seeker" && currentUser?.trainee) {
        return category.categoryKey !== "facturation";
      }
      return true;
    })
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        // Filter out promoCodes if user doesn't have permission
        if (
          item.labelKey === "provider.promoCodes" &&
          !currentUser?.promo_permission
        ) {
          return false;
        }
        // Filter out specific items for master seeker
        if (userType === "seeker" && currentUser?.master_seeker) {
          const itemsToHide = [
            "seeker.myMessages",
            "seeker.myRatings",
            "provider.bookingInvoice", // This might be "seeker.bookingInvoice" depending on your constants
          ];
          if (itemsToHide.includes(item.labelKey)) {
            return false;
          }
        }
        // Filter out training invoices for non-master seekers
        if (item.masterSeekerOnly && !currentUser?.master_seeker) {
          return false;
        }
        return true;
      }),
    }));

  // Logout handler
  const handleLogout = async () => {
    await dispatch(logout({ language })); // Pass language to the logout thunk
    if (isMobileView && closeSidebar) {
      closeSidebar(); // Close the sidebar only if in mobile view
    }
    navigate("/"); // Redirect to home after logout
  };

  // Use the `t` function to translate category and item labels dynamically
  const menuCategories = selectedMenuCategories.map((category) => ({
    category: t(`dashboard.${category.categoryKey}`), // Translating category
    categoryIcon: category.categoryIcon,
    items: category.items.map((item) => ({
      label: t(`dashboard.${item.labelKey}`), // Translating item label
      icon: item.icon,
      route: item.route,
    })),
  }));

  const handleItemClick = (route) => {
    if (route.startsWith("http")) {
      window.open(route, "_blank"); // Open the external URL in a new tab
    } else {
      navigate(route); // For internal routes, navigate as usual
    }
    if (isMobileView && closeSidebar) {
      closeSidebar(); // Close the sidebar only if in mobile view
    }
  };

  return (
    <div
      className={`${styles.dashboardMenu} ${isMobileView ? styles.mobile : ""}`}
    >
      <nav className={styles.menu}>
        {menuCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className={styles.menuCategory}>
            <div className={styles.categoryTitle}>
              <category.categoryIcon
                className={styles.categoryIcon}
                fill="ffff"
                stroke="ffff"
              />
              <p>{category.category}</p>
            </div>
            {category.items.map((item, itemIndex) => (
              <div
                key={itemIndex}
                className={`${styles.menuItem} ${
                  location.pathname === item.route ? styles.active : ""
                }`}
                onClick={() => handleItemClick(item.route)}
              >
                <item.icon
                  // fill={location.pathname === item.route ? "#f06292" : "#000"}
                  className={styles.menuItemIcon}
                />
                <p>{item.label}</p>
              </div>
            ))}
          </div>
        ))}
      </nav>

      <>
        {userType === "seeker" ? (
          <div
            className={styles.reserveSpaceBtn}
            onClick={() => {
              navigate("/search");
              if (isMobileView && closeSidebar) {
                closeSidebar(); // Close the sidebar only if in mobile view
              }
            }}
          >
            <p>{t("dashboard.seeker.reserveSpace")}</p>
          </div>
        ) : (
          <div
            className={styles.reserveSpaceBtn}
            onClick={() => {
              navigate("/calendar/timetable");
              if (isMobileView && closeSidebar) {
                closeSidebar(); // Close the sidebar only if in mobile view
              }
            }}
          >
            <p>{t("dashboard.provider.agenda")}</p>
          </div>
        )}
        <div className={styles.logoutBtn} onClick={handleLogout}>
          <p>{t("dashboard.logout")}</p>
        </div>
      </>
    </div>
  );
};

export default DashboardMenu;
